/**
 * Canonical icon catalog (doc 147 §8.10).
 *
 * Single source of truth mapping Atlas concepts → Material Symbols Rounded
 * ligature names. We deliver Material Symbols as a **font** (loaded in
 * index.html) rather than via `@mui/icons-material` — same icon vocabulary and
 * names, but no `@mui/material` peer dependency and a lighter bundle, which the
 * §8.7 perf budget cares about. The `<Icon>` component renders
 * `<span class="material-symbols-rounded">{name}</span>`.
 */

import domainUiJson from '@atlas-manifest/domain-ui.json';

/** Domain id (manifest `Domain.id`) → Material Symbol. Sourced from domain-ui.json. */
export const DOMAIN_ICONS: Record<string, string> = Object.fromEntries(
  Object.entries(domainUiJson.domains).map(([id, cfg]) => [id, cfg.icon]),
);

/** Entity-shape kinds (doc 147 §8.10.5). */
export type EntityShape =
  | 'scalar'
  | 'collection'
  | 'fsm'
  | 'aggregate'
  | 'view'
  | 'config'
  | 'audit'
  | 'archive';

export const ENTITY_SHAPE_ICONS: Record<EntityShape, string> = {
  scalar: 'description',
  collection: 'folder_open',
  fsm: 'account_tree',
  aggregate: 'analytics',
  view: 'visibility',
  config: 'tune',
  audit: 'history',
  archive: 'archive',
};

/** Surface-kind icons (doc 147 §8.10.6). */
export const SURFACE_ICONS: Record<string, string> = {
  card: 'credit_card',
  chart: 'insert_chart',
  overlay: 'layers',
  sheet: 'layers',
  chip: 'label',
  ai: 'auto_awesome',
  hero: 'star',
  list: 'touch_app',
  banner: 'do_not_disturb_on',
  grid: 'grid_view',
  other: 'do_not_disturb_on',
};

/** Action icons (doc 147 §8.10.7). */
export const ACTION_ICONS = {
  search: 'search',
  filter: 'filter_list',
  pin: 'push_pin',
  share: 'link',
  export: 'file_download',
  openInSource: 'open_in_new',
  trace: 'route',
  impact: 'change_history',
  sandbox: 'fork_right',
  aiAsk: 'auto_awesome',
  annotate: 'edit_note',
  settings: 'settings',
  close: 'close',
  back: 'arrow_back',
  signOut: 'logout',
} as const;

/** Status / severity icons (doc 147 §8.10.8). */
export const STATUS_ICONS = {
  healthy: 'check_circle',
  info: 'info',
  warning: 'warning',
  error: 'error',
  drift: 'sync_problem',
  phantom: 'help',
  resolved: 'task_alt',
  inProgress: 'pending',
  draft: 'edit',
  locked: 'lock',
  suppressed: 'visibility_off',
} as const;

/**
 * Infer an entity's shape from its manifest fields. Heuristic, ordered by
 * specificity — used to pick a shape icon in lists and the detail view.
 */
export function inferEntityShape(entity: {
  tags: string[];
  isEphemeral: boolean;
  firestorePath?: string | undefined;
  fields: Array<{ name: string; type: string }>;
}): EntityShape {
  const tags = entity.tags.map((t) => t.toLowerCase());
  const path = entity.firestorePath ?? '';
  if (path.startsWith('/config/')) return 'config';
  if (tags.some((t) => t.includes('audit')) || /audit|log/i.test(path)) return 'audit';
  if (tags.some((t) => t.includes('archive'))) return 'archive';
  if (tags.some((t) => t.includes('fsm') || t.includes('state machine'))) return 'fsm';
  if (entity.fields.some((f) => /^status$|state$/i.test(f.name))) return 'fsm';
  if (tags.some((t) => t.includes('aggregate') || t.includes('computed'))) return 'aggregate';
  if (/aggregation|summary/i.test(path)) return 'aggregate';
  if (entity.isEphemeral) return 'view';
  if (path.includes('{') && path.split('{').length > 2) return 'collection';
  return 'scalar';
}

/** Look up a domain icon, defaulting to `hub` for unknown domains. */
export function domainIcon(domainId: string): string {
  return DOMAIN_ICONS[domainId] ?? 'hub';
}

/** Look up a surface icon, defaulting to the stub icon. */
export function surfaceIcon(type: string): string {
  return SURFACE_ICONS[type] ?? 'do_not_disturb_on';
}
